import type { GraphQLAuditItem, GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { ContentCard, Text } from '@soteria/design-system-ve';
import { useState, useCallback, useRef, useMemo } from 'react';

import * as styles from './AuditItem.css';
import { AuditItemActions } from './components/AuditItemActions';
import { AuditItemContent } from './components/AuditItemContent';
import { AuditItemDecisions } from './components/AuditItemDecisions';
import { DecisionStats } from './components/DecisionStats';

interface AuditItemProps {
  item: GraphQLAuditItem;
  decisions?: GraphQLAuditDecision[];
  onSkipToggle?: (itemId: string, isSkipped: boolean) => void;
  onDecisionSubmit?: (itemId: string, hasDecision: boolean) => void;
  onDecisionSelect?: (decisionId: string) => void;
  onItemSelect?: (item: GraphQLAuditItem) => void;
  isSelected?: boolean; // Whether this item is currently selected
  dialogLoading?: boolean; // Loading state from external dialog/panel
  isSessionCompleted?: boolean; // Whether the audit session is completed (disables interactions)
  onContext?: (item: GraphQLAuditItem) => void; // Open conversation context modal for this item
}

export function AuditItem({
  item,
  decisions = [],
  onSkipToggle,
  onDecisionSelect,
  onItemSelect,
  isSelected = false,
  dialogLoading = false,
  isSessionCompleted = false,
  onContext,
}: AuditItemProps) {
  const [likeButtonLoading, setLikeButtonLoading] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Determine if content is safe based on classification results
  const isSafeContent = useMemo(() => {
    const results = item.contentSnapshot?.classificationResults || [];

    return results.some(result => result.name.toLowerCase().includes('safe'));
  }, [item.contentSnapshot?.classificationResults]);

  // Blur state management for unsafe content
  const [isContentBlurred, setIsContentBlurred] = useState(!isSafeContent);

  // Check if any decision operations are in progress
  // Note: skipLoading is excluded because skip operations are optimistic and shouldn't show loading state
  const isDecisionLoading = likeButtonLoading || dialogLoading;

  // Get current user's decisions for the actions component
  const currentUserDecision = useMemo(() => {
    const currentUserDecisions = decisions.filter(decision => decision.isCurrentUser);

    return currentUserDecisions[0]; // For backward compatibility with actions component
  }, [decisions]);

  // Toggle blur state
  const handleToggleBlur = useCallback(() => {
    setIsContentBlurred(!isContentBlurred);
  }, [isContentBlurred]);

  // Handle item click for selection
  const handleItemClick = useCallback(() => {
    if (isSessionCompleted) {
      return; // Don't allow selection for completed sessions
    }

    onItemSelect?.(item);
  }, [item, onItemSelect, isSessionCompleted]);

  // Handle item selection for side panel with event propagation prevention
  const handleOpenDecisionPanel = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card selection
      if (isSessionCompleted) {
        return; // Don't allow selection for completed sessions
      }

      onItemSelect?.(item);
    },
    [item, onItemSelect, isSessionCompleted]
  );

  // Handle context button click
  const handleContextClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card selection
      onContext?.(item);
    },
    [item, onContext]
  );

  return (
    <ContentCard ref={cardRef} className={styles.auditItemCard} data-selected={isSelected} isFocused={isSelected} tabIndex={0} onClick={handleItemClick}>
      {/* Selection Overlay */}
      <div className={styles.selectionOverlay} />

      {/* Content Summary Container */}
      <AuditItemContent isContentBlurred={isContentBlurred} item={item} onToggleBlur={handleToggleBlur} />

      {/* Decisions Section with Header and Actions */}
      <div className={styles.decisionsSection}>
        <div className={styles.decisionsHeader}>
          <div className={styles.decisionsInfo}>
            <Text className={styles.decisionsTitle}>{`Audit Decisions (${decisions.length})`}</Text>
            <DecisionStats decisions={decisions} />
          </div>
          <div className={styles.decisionsActions}>
            <AuditItemActions
              currentUserDecision={currentUserDecision}
              isSessionCompleted={isSessionCompleted}
              item={item}
              onContext={onContext ? handleContextClick : undefined}
              onLikeButtonLoadingChange={setLikeButtonLoading}
              onOpenDecisionPanel={handleOpenDecisionPanel}
              onSkipToggle={onSkipToggle}
            />
          </div>
        </div>

        {/* Decisions List and Toggle */}
        <AuditItemDecisions
          decisions={decisions}
          isDecisionLoading={isDecisionLoading}
          isSessionCompleted={isSessionCompleted}
          item={item}
          onDecisionSelect={onDecisionSelect}
        />
      </div>
    </ContentCard>
  );
}
